import type { Feature, Polygon } from "geojson";
import type { MapTool, LassoMetadata, LassoFeatureType } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { lassoSelectUseCase } from "../../../application/map/useCases/lassoSelectUseCase";
import type { FeatureId } from "../engine/useMapDrawEngine";

export interface LassoToolConfig {
	getLassoMetadata: () => LassoMetadata[];
	onLassoComplete?: (selectedIds: string[]) => void;
	/** When provided, shows a type picker before starting freehand.
	 *  Skipped when only one feature type is available. */
	requestFeatureType?: (
		availableTypes: LassoFeatureType[],
		onSelect: (type: LassoFeatureType) => void,
		onCancel: () => void,
	) => void;
}

export function makeLassoTool(deps: ToolDeps, config: LassoToolConfig): MapTool {
	let metadata: LassoMetadata[] = [];
	let featureTypeFilter: LassoFeatureType | null = null;

	function startFreehand() {
		deps.drawEngine.setMode("freehand");
		deps.drawEngine.setFinishHandler(onFinish);
	}

	function onFinish(_id: FeatureId, _ctx: { mode: string; action: string }) {
		const snapshot = deps.drawEngine.getSnapshot();
		const lassoFeature = snapshot.find((f) => f.geometry.type === "Polygon");
		if (!lassoFeature) return;

		const lasso: Feature<Polygon> = {
			type: "Feature",
			properties: {},
			geometry: lassoFeature.geometry as Polygon,
		};

		const selectedIds = lassoSelectUseCase({
			lasso,
			candidates: metadata,
			featureTypeFilter: featureTypeFilter ?? undefined,
		});
		config.onLassoComplete?.(selectedIds);
		deps.drawEngine.clear();
	}

	return {
		id: "lasso",
		label: "Lasso",
		cursor: "crosshair",
		onActivate() {
			metadata = config.getLassoMetadata();
			if (metadata.length === 0) {
				deps.showError("No features available");
				deps.deactivateTool();
				return;
			}

			const uniqueTypes = [...new Set(metadata.map((m) => m.featureType))];

			if (uniqueTypes.length > 1 && config.requestFeatureType) {
				config.requestFeatureType(
					uniqueTypes,
					(selectedType) => {
						featureTypeFilter = selectedType;
						startFreehand();
					},
					() => {
						deps.deactivateTool();
					},
				);
			} else {
				featureTypeFilter = uniqueTypes.length === 1 ? uniqueTypes[0] : null;
				startFreehand();
			}
		},
		onDeactivate() {
			deps.drawEngine.setFinishHandler(null);
			deps.drawEngine.clear();
			deps.drawEngine.setMode("static");
			metadata = [];
			featureTypeFilter = null;
		},
	};
}
